import { Tab as TabRoot } from "@headlessui/react";
import { ReactNode, useState } from "react";
import { TabProps } from "@common/types";
import { cn } from "@app/lib/utils";

type TabRootType = typeof TabRoot;

function Group<T extends { value: string | number; label: ReactNode }>({
  children,
  tabs,
  onChange,
  selectedIndex,
  defaultIndex,
  ...rest
}: TabProps<T>) {
  const [internalSelectedIndex, setInternalSelectedIndex] = useState<
    number | undefined
  >(defaultIndex ?? 0);

  const activeIndex = selectedIndex ?? internalSelectedIndex ?? 0;

  return (
    <>
      <TabRoot.Group
        selectedIndex={activeIndex}
        {...rest}
        onChange={(index) => {
          setInternalSelectedIndex(index);
          if (tabs[index]) {
            onChange?.(tabs[index]);
          }
        }}
      >
        <>
          <TabRoot.List className="relative z-0 flex min-w-0 items-center gap-2 overflow-x-auto pb-1 [-webkit-overflow-scrolling:touch] [scrollbar-width:none] sm:items-end sm:gap-6 sm:overflow-visible sm:pb-0 sm:after:absolute sm:after:inset-x-0 sm:after:bottom-0 sm:after:h-0.5 sm:after:bg-border [&::-webkit-scrollbar]:hidden">
            {tabs.map((tab) => (
              <TabRoot
                key={tab.value}
                className={({ selected }) =>
                  cn(
                    "group relative z-10 flex h-10 flex-shrink-0 items-center rounded-md border px-3 text-center text-sm font-bold transition-all duration-150 focus:z-20 focus:outline-none sm:h-auto sm:rounded-none sm:border-0 sm:bg-transparent sm:px-1 sm:py-3 sm:font-semibold sm:shadow-none sm:hover:bg-transparent",
                    selected
                      ? "border-primary/25 bg-primary/10 text-primary shadow-soft sm:text-foreground"
                      : "border-border bg-card text-muted-foreground hover:border-foreground/20 hover:bg-muted hover:text-foreground sm:border-transparent sm:text-muted-foreground",
                  )
                }
              >
                {({ selected }) => (
                  <>
                    <span className="truncate">{tab.label}</span>
                    {selected && (
                      <span
                        aria-hidden="true"
                        className="absolute inset-x-0 bottom-0 z-20 hidden h-0.5 rounded-full bg-primary sm:block"
                      />
                    )}
                  </>
                )}
              </TabRoot>
            ))}
          </TabRoot.List>
          {children}
        </>
      </TabRoot.Group>
    </>
  );
}

function Panels(props: Parameters<TabRootType["Panels"]>[0]) {
  return <TabRoot.Panels {...props} />;
}

function Panel(props: Parameters<TabRootType["Panel"]>[0]) {
  return <TabRoot.Panel {...props} />;
}

export const Tab = Object.assign(Group, { Panels, Panel });
